import { StyledComponentProps } from "@mui/styles";
import * as api from "api/Knowledge";
import { FeedClassKey } from "assets/jss/common-styles";
import CardButtonFavorite from "components/Buttons/CardButtonFavorite";
import CardButtonSeeAlso from "components/Buttons/CardButtonSeeAlso";
import CardButtonShare from "components/Buttons/CardButtonShare";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import AppMessage from "components/Translations/AppMessage";
import { PublicationShareType } from "models/ApiEnums";
import { FeedItemDto, FeedItemInteractionDto, MemberDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import FavoritesDrawer from "views/Timeline/Dialogs/FavoritesDrawer";
import PublicationShareDrawer from "views/Timeline/Dialogs/PublicationShareDrawer";
import RelationsDrawer from "views/Timeline/Dialogs/RelationsDrawer";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    dialog: any;
}

interface IProps {
    data: FeedItemDto;
    fullView: boolean;
    showRelationsButton: boolean;
    showShareButton: boolean;
    showAddToFavorites: boolean;
    onUpdateCard: (data: DeepPartial<FeedItemDto>) => void;
    onSidebarResetRequest: () => void;
}

type Props = IProps & StyledComponentProps<FeedClassKey> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class ActionsDefaultRight extends React.PureComponent<Props, IState> {

    private handlePublicationId: string;
    private favorited: boolean;
    private hasRelations: boolean;

    constructor(props: Props) {
        super(props);
        this.state = {
            dialog: null
        };
    }

    ///
    private postShare(members: MemberDto[], message: string) {
        const { t } = this.props;

        api.postShare(
            {
                publicationId: this.handlePublicationId,
                members,
                message,
                shareType: PublicationShareType.Recommendation
            }
        )
            .then((result) => {
                if (result) this.props.snackbar.showMessage(t("Common.Feed.Messages.RecommendationSuccess"), "success");
                else this.props.snackbar.showMessage(t("Common.Feed.Messages.RecommendationFailed", "error"));
            })
            .catch(() => {
                this.props.snackbar.showMessage(<AppMessage resource="OperationFailed" />, "error");
            });
    }

    ///
    private handleCloseDialog = () => {
        this.setState({ dialog: null });
    };

    ///
    private handleFavorite = () => {

        this.setState({
            dialog: <FavoritesDrawer
                classes={this.props.classes}
                publicationId={this.handlePublicationId}
                onUpdate={
                    (data: DeepPartial<FeedItemInteractionDto>, syncSidebar: boolean) => {

                        if (this.handlePublicationId != this.props.data.publicationId)
                            this.props.onUpdateCard(
                                {
                                    publicationId: this.props.data.publicationId,
                                    bestAnswer: { interaction: data }
                                });
                        else
                            this.props.onUpdateCard(
                                {
                                    publicationId: this.props.data.publicationId,
                                    interaction: data
                                });

                        if (syncSidebar) {
                            this.props.onSidebarResetRequest();
                        }
                    }
                }
                onClose={this.handleCloseDialog} />
        });
    };

    ///
    private handleShare = () => {
        this.setState({
            dialog: (
                <PublicationShareDrawer
                    publicationId={this.props.data.publicationId}
                    hasMemberRestriction={this.props.data.publication.topics.every(x => x.hasMemberRestriction)}
                    onClose={(result) => {
                        if (result) this.postShare(
                            result.members.map((v) => { return { memberId: v.key, memberType: v.memberType } as MemberDto; }),
                            result.comment);
                        this.handleCloseDialog();
                    }}
                />
            )
        });
    };

    ///
    private handleRelations = () => {
        this.setState({
            dialog: (
                <RelationsDrawer
                    publicationId={this.handlePublicationId}
                    onClose={this.handleCloseDialog}
                />
            )
        });
    };

    ///
    public render() {
        const {
            t,
            data: {
                publicationId,
                publication: {
                    hasRelations
                },
                interaction: {
                    favorited,
                    canAddToFavorites,
                    canSharePublication
                },
                bestAnswer
            },
            fullView
        } = this.props;


        if (fullView || bestAnswer == null) {
            this.handlePublicationId = publicationId;
            this.favorited = favorited;
            this.hasRelations = hasRelations;
        }
        else {
            this.handlePublicationId = bestAnswer.publicationId;
            this.favorited = bestAnswer.interaction.favorited;
            this.hasRelations = false;
        }

        return (
            <>
                {
                    this.props.showAddToFavorites && canAddToFavorites &&
                    <CardButtonFavorite
                        title={t("Common.Feed.Actions.ButtonAddToFavoritesTitle")}
                        pressed={this.favorited}
                        color={this.favorited === true ? "warning" : "inherit"}
                        onClick={this.handleFavorite}
                    />
                }

                {
                    this.props.showShareButton && canSharePublication &&
                    <CardButtonShare
                        onClick={this.handleShare}
                    />
                }

                {
                    this.props.showRelationsButton && this.hasRelations
                        ? (
                            <CardButtonSeeAlso
                                color={"pressed"}
                                title={this.props.t("Views.Publication.Widgets.WidgetRelations.BoxTitle")}
                                onClick={this.handleRelations}
                            />
                        )
                        : null
                }

                {this.state.dialog}
            </>
        );
    }
}

export default withSnackbar(withTranslation()(ActionsDefaultRight));
